import { useCallback, useRef, useState } from 'react';
import type { SceneSelection } from '../../scenes/types.js';

export type AdjustMode = 'reset' | 'increase';
export interface AdjustRule {
  mode: AdjustMode;
  pct: number;
}
export interface BetLimits {
  min: bigint;
  max: bigint;
  step: bigint;
}
export interface AutoConfig {
  numberOfBets: number; // 0 = infinite
  onWin: AdjustRule;
  onLoss: AdjustRule;
  stopProfit: bigint | null;
  stopLoss: bigint | null;
}
export interface RoundResult {
  win: boolean;
  payout: bigint; // minor units credited (0 on loss)
}

export function snapshotSelection(get: () => SceneSelection | undefined): SceneSelection | undefined {
  return get();
}

function snap(v: bigint, limits: BetLimits): bigint {
  let x = v;
  if (x < limits.min) x = limits.min;
  if (x > limits.max) x = limits.max;
  // Floor onto the step grid: the backend's assertBetWithinLimits rejects any
  // range-limit bet that isn't min + k*step (ORIG-002), so an auto-derived %
  // raise MUST land on the grid or the round is refused. Flooring (not rounding)
  // keeps the wager <= intended; a sub-step raise off the minimum bet may not
  // move until the percentage compounds past one step.
  if (limits.step > 0n) x = limits.min + ((x - limits.min) / limits.step) * limits.step;
  return x;
}

export function nextBet(args: {
  current: bigint;
  base: bigint;
  won: boolean;
  rule: AdjustRule;
  limits: BetLimits;
}): bigint {
  const { current, base, rule, limits } = args;
  if (rule.mode === 'reset') return snap(base, limits);
  const raised = current + (current * BigInt(Math.round(rule.pct))) / 100n;
  return snap(raised, limits);
}

export function shouldStop(args: {
  remaining: number; // bets left after the one just played; <0 means infinite
  profit: bigint;
  stopProfit: bigint | null;
  stopLoss: bigint | null;
}): boolean {
  if (args.remaining === 0) return true;
  if (args.stopProfit != null && args.profit >= args.stopProfit) return true;
  if (args.stopLoss != null && -args.profit >= args.stopLoss) return true;
  return false;
}

export interface UseAutoBet {
  running: boolean;
  profit: bigint;
  remaining: number | null;
  start: (cfg: AutoConfig) => void;
  stop: () => void;
}

/**
 * Drives auto-bet by looping an injected `playRound` (which performs one bet and
 * resolves only after the scene reveal). Bet sizing/stop logic is the pure
 * functions above; this wrapper holds React state and the cancel flag.
 */
export function useAutoBet(opts: {
  baseBet: () => bigint;
  limits: BetLimits;
  balance: () => bigint | null;
  playRound: (bet: bigint, selection?: SceneSelection) => Promise<RoundResult>;
  selection: () => SceneSelection | undefined;
  onInsufficient: () => void;
}): UseAutoBet {
  const [running, setRunning] = useState(false);
  const [profit, setProfit] = useState(0n);
  const [remaining, setRemaining] = useState<number | null>(null);
  const cancel = useRef(false);

  const stop = useCallback(() => {
    cancel.current = true;
    setRunning(false);
  }, []);

  const start = useCallback(
    (cfg: AutoConfig) => {
      if (running) return;
      cancel.current = false;
      setRunning(true);
      setProfit(0n);
      setRemaining(cfg.numberOfBets > 0 ? cfg.numberOfBets : null);

      void (async () => {
        const base = opts.baseBet();
        const stickySelection = snapshotSelection(opts.selection);
        let bet = snap(base, opts.limits);
        let runProfit = 0n;
        let left = cfg.numberOfBets > 0 ? cfg.numberOfBets : -1;

        while (!cancel.current) {
          const bal = opts.balance();
          if (bal != null && bal < bet) {
            opts.onInsufficient();
            break;
          }
          let result: RoundResult;
          try {
            result = await opts.playRound(bet, stickySelection);
          } catch {
            break;
          }
          if (cancel.current) break;

          runProfit += result.payout - bet;
          setProfit(runProfit);
          if (left > 0) {
            left -= 1;
            setRemaining(left);
          }

          if (shouldStop({ remaining: left < 0 ? -1 : left, profit: runProfit, stopProfit: cfg.stopProfit, stopLoss: cfg.stopLoss })) {
            break;
          }
          bet = nextBet({
            current: bet,
            base,
            won: result.win,
            rule: result.win ? cfg.onWin : cfg.onLoss,
            limits: opts.limits,
          });
        }
        cancel.current = true;
        setRunning(false);
      })();
    },
    [running, opts],
  );

  return { running, profit, remaining, start, stop };
}
